// Client side of biometric unlock (WebAuthn + PRF). Hand-rolled so we control the
// PRF extension precisely; the server (@simplewebauthn/server) verifies signatures.

function bufToB64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = ''; for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64urlToBuf(s: string): Uint8Array {
  const t = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = t.length % 4 ? '='.repeat(4 - (t.length % 4)) : '';
  const bin = atob(t + pad); const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
const post = (url: string, body: unknown) =>
  fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

export function webauthnSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
}

/** Does this device have a usable biometric (platform authenticator)? */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try { return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { return false; }
}

const DEVICE_FLAG = 'sor_bio_device';
const SKIP_FLAG = 'sor_bio_skip';
export function biometricSetUpOnThisDevice(): boolean { try { return localStorage.getItem(DEVICE_FLAG) === '1'; } catch { return false; } }
export function biometricOfferDismissed(): boolean { try { return localStorage.getItem(SKIP_FLAG) === '1'; } catch { return false; } }
export function dismissBiometricOffer(): void { try { localStorage.setItem(SKIP_FLAG, '1'); } catch { /* */ } }

/** Is biometric unlock enrolled for this vault (any credential)? */
export async function biometricEnrolled(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try { return (await fetch('/api/auth/webauthn/available').then((r) => r.json())).enabled === true; } catch { return false; }
}

const prfResult = (cred: PublicKeyCredential): ArrayBuffer | undefined =>
  (cred.getClientExtensionResults() as any)?.prf?.results?.first;

/** Enroll this device's biometric. Must be called while the vault is unlocked. */
export async function enrollBiometric(label: string): Promise<void> {
  if (!webauthnSupported()) throw new Error('This browser does not support biometric unlock.');
  const { options, prfSalt } = await post('/api/auth/webauthn/register/options', {}).then((r) => r.json());
  const saltBuf = b64urlToBuf(prfSalt);

  const create: CredentialCreationOptions = {
    publicKey: {
      ...options,
      challenge: b64urlToBuf(options.challenge),
      user: { ...options.user, id: b64urlToBuf(options.user.id) },
      excludeCredentials: (options.excludeCredentials ?? []).map((c: any) => ({ ...c, id: b64urlToBuf(c.id) })),
      extensions: { ...(options.extensions ?? {}), prf: { eval: { first: saltBuf } } },
    } as any,
  };
  const cred = (await navigator.credentials.create(create)) as PublicKeyCredential | null;
  if (!cred) throw new Error('Setup was cancelled.');

  // Get the PRF secret. Some platforms return it at create; otherwise do a quick
  // assertion against the new credential to obtain it.
  let prf = prfResult(cred);
  if (!prf) {
    const get: CredentialRequestOptions = {
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [{ id: cred.rawId, type: 'public-key' }],
        userVerification: 'required',
        extensions: { prf: { eval: { first: saltBuf } } } as any,
      },
    };
    const asrt = (await navigator.credentials.get(get)) as PublicKeyCredential | null;
    prf = asrt ? prfResult(asrt) : undefined;
  }
  if (!prf) throw new Error('Your device/browser did not provide a biometric key (PRF). Biometric unlock is not available here — keep using your passphrase.');

  const att = cred.response as AuthenticatorAttestationResponse;
  const response = {
    id: cred.id, rawId: bufToB64url(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufToB64url(att.clientDataJSON),
      attestationObject: bufToB64url(att.attestationObject),
      transports: att.getTransports ? att.getTransports() : [],
    },
    clientExtensionResults: {},
    authenticatorAttachment: (cred as any).authenticatorAttachment ?? undefined,
  };
  const r = await post('/api/auth/webauthn/register/verify', { challenge: options.challenge, response, prfOutput: bufToB64url(prf), prfSalt, label });
  if (!r.ok) throw new Error((await r.json()).error ?? 'Could not save biometric unlock.');
  try { localStorage.setItem('sor_bio_device', '1'); } catch { /* */ }
}

/** Unlock the vault with a biometric (no passphrase). */
export async function unlockBiometric(): Promise<void> {
  if (!webauthnSupported()) throw new Error('This browser does not support biometric unlock.');
  const { options, prfByCredential } = await post('/api/auth/webauthn/login/options', {}).then((r) => r.json());
  const evalByCredential: Record<string, { first: Uint8Array }> = {};
  for (const [credId, salt] of Object.entries(prfByCredential as Record<string, string>)) evalByCredential[credId] = { first: b64urlToBuf(salt) };

  const get: CredentialRequestOptions = {
    publicKey: {
      ...options,
      challenge: b64urlToBuf(options.challenge),
      allowCredentials: (options.allowCredentials ?? []).map((c: any) => ({ ...c, id: b64urlToBuf(c.id) })),
      extensions: { ...(options.extensions ?? {}), prf: { evalByCredential } } as any,
    } as any,
  };
  const cred = (await navigator.credentials.get(get)) as PublicKeyCredential | null;
  if (!cred) throw new Error('Unlock was cancelled.');
  const prf = prfResult(cred);
  if (!prf) throw new Error('No biometric key returned by this device.');

  const asr = cred.response as AuthenticatorAssertionResponse;
  const response = {
    id: cred.id, rawId: bufToB64url(cred.rawId), type: cred.type,
    response: {
      clientDataJSON: bufToB64url(asr.clientDataJSON),
      authenticatorData: bufToB64url(asr.authenticatorData),
      signature: bufToB64url(asr.signature),
      userHandle: asr.userHandle ? bufToB64url(asr.userHandle) : undefined,
    },
    clientExtensionResults: {},
    authenticatorAttachment: (cred as any).authenticatorAttachment ?? undefined,
  };
  const r = await post('/api/auth/webauthn/login/verify', { challenge: options.challenge, response, prfOutput: bufToB64url(prf) });
  if (!r.ok) throw new Error((await r.json()).error ?? 'Biometric unlock failed.');
}
